import React, { useState, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui-custom/Button';
import { Loader2, Upload } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useAuth as useClerkAuth, useOrganization } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { replaceFloorPlanImage } from '@/services/floorPlanService';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { toast } from 'sonner';
import { PDFPageSelectionModal } from './PDFPageSelectionModal';
import { processPDFPage, DEFAULT_SCALE } from '@/utils/pdfUtils';
import { PDFPage } from '@/types/floorPlan';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { configurePDFWorker } from '@/utils/pdfWorkerConfig';
import { queryKeys } from '@/lib/queryKeys';

interface FloorPlanReplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplacementComplete: () => Promise<void>;
  floorPlan: {
    id: string;
    name: string;
    file_path: string;
    file_type: string;
    project_id: string;
  };
}

export function FloorPlanReplaceModal({
  isOpen,
  onClose,
  onReplacementComplete,
  floorPlan,
}: FloorPlanReplaceModalProps) {
  // Check if using Clerk
  const isUsingClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const clerkAuth = useClerkAuth();
  const { organization } = useOrganization();
  const supabaseAuth = useAuth();
  const queryClient = useQueryClient();

  // Get user ID based on auth type
  // For Clerk, use the organization ID as the user ID for file paths
  const userId = isUsingClerk
    ? organization?.id || clerkAuth?.userId || 'unknown'
    : supabaseAuth.user?.id || 'unknown';

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [pdfPages, setPDFPages] = useState<PDFPage[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const pdfjsLibRef = useRef<any>(null);

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCurrentFile(file);

    if (file.type === 'application/pdf') {
      await handlePDFUpload(file);
    } else if (file.type.startsWith('image/')) {
      handleImageUpload(file);
    } else {
      toast.error('Please upload a PDF or image file.');
    }
  };

  const handlePDFUpload = async (file: File) => {
    setIsLoading(true);
    setProgress(0);
    setLoadingMessage('Loading PDF library...');

    try {
      const pdfjsLib = await configurePDFWorker();
      pdfjsLibRef.current = pdfjsLib;

      setLoadingMessage('Processing PDF pages...');

      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const loadingTask = pdfjsLib.getDocument({
        data: uint8Array,
        cMapUrl: '/pdfjs-dist/cmaps/',
        cMapPacked: true,
      });

      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;
      const pages: PDFPage[] = [];

      for (let i = 1; i <= totalPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        if (context) {
          await page.render({
            canvasContext: context,
            viewport: viewport,
            background: 'white',
          }).promise;
        }

        pages.push({
          pageNumber: i,
          thumbnail: canvas.toDataURL(),
          selected: i === 1, // Select first page by default
          name: `${floorPlan.name} - Page ${i}`,
        });

        setProgress((i / totalPages) * 100);
      }

      setPDFPages(pages);
      setIsPDFModalOpen(true);
    } catch (error) {
      logger.error('Error processing PDF:', error);
      toast.error('There was an error processing your PDF file. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handlePageSelect = (pageNumber: number) => {
    setPDFPages(pages =>
      pages.map(page =>
        page.pageNumber === pageNumber ? { ...page, selected: !page.selected } : page,
      ),
    );
  };

  const handlePageNameChange = (pageNumber: number, name: string) => {
    setPDFPages(pages =>
      pages.map(page => (page.pageNumber === pageNumber ? { ...page, name } : page)),
    );
  };

  const handlePDFContinue = async () => {
    if (!userId || userId === 'unknown') {
      toast.error('You must be logged in to replace floor plans.');
      return;
    }

    setIsPDFModalOpen(false);
    setIsLoading(true);
    setProgress(0);

    const selectedPages = pdfPages.filter(p => p.selected);

    // Only process the first selected page for replacement
    if (selectedPages.length === 0) {
      toast.error('Please select a page to use as the replacement.');
      setIsLoading(false);
      return;
    }

    const selectedPage = selectedPages[0];

    try {
      // Fetch the original floor plan metadata to preserve dimensions
      setLoadingMessage('Fetching original floor plan metadata...');
      const supabase = await getAuthenticatedSupabaseClient();
      const { data: originalMetadata } = await supabase
        .from('floor_plan_metadata')
        .select('image_width, image_height')
        .eq('floor_plan_id', floorPlan.id)
        .maybeSingle();

      logger.log('Original floor plan metadata:', originalMetadata);

      // Load the PDF once
      setLoadingMessage('Processing selected page...');
      const arrayBuffer = await currentFile!.arrayBuffer();
      const pdfjsLib = pdfjsLibRef.current || (await configurePDFWorker());
      pdfjsLibRef.current = pdfjsLib;

      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        cMapUrl: '/pdfjs-dist/cmaps/',
        cMapPacked: true,
      }).promise;

      // Get and process the page with auto-cropping
      const pdfPage = await pdf.getPage(selectedPage.pageNumber);
      const processedCanvas = await processPDFPage(pdfPage, DEFAULT_SCALE); // Higher scale for final output (~200 DPI)

      // Convert to PNG blob
      const blob = await new Promise<Blob>(resolve => {
        processedCanvas.toBlob(blob => {
          if (blob) resolve(blob);
        }, 'image/png');
      });

      // Create a File object from the blob
      const newFile = new File([blob], `${selectedPage.name}.png`, { type: 'image/png' });

      setLoadingMessage('Uploading replacement image...');

      // Replace the floor plan
      const { success, error } = await replaceFloorPlanImage(
        floorPlan.id,
        floorPlan.file_path,
        newFile,
        userId,
        floorPlan.project_id,
      );

      if (!success || error) {
        throw error || new Error('Failed to replace floor plan');
      }

      // Invalidate React Query cache to force UI refresh with new image
      logger.log('[FloorPlanReplace] Invalidating floor plan cache for:', floorPlan.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.floorPlan(floorPlan.id) });

      toast.success('Floor plan replaced successfully.');
      await onReplacementComplete();
      onClose();
    } catch (error) {
      logger.error('Error replacing floor plan:', error);
      toast.error('There was an error replacing your floor plan. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
      setPDFPages([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setCurrentFile(null);
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!userId || userId === 'unknown') {
      toast.error('You must be logged in to replace floor plans.');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Uploading replacement image...');

    try {
      const { success, error } = await replaceFloorPlanImage(
        floorPlan.id,
        floorPlan.file_path,
        file,
        userId,
        floorPlan.project_id,
      );

      if (!success || error) {
        throw error || new Error('Failed to replace floor plan');
      }

      // Invalidate React Query cache to force UI refresh with new image
      logger.log('[FloorPlanReplace] Invalidating floor plan cache for:', floorPlan.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.floorPlan(floorPlan.id) });

      toast.success('Floor plan replaced successfully.');
      await onReplacementComplete();
      onClose();
    } catch (error) {
      logger.error('Error replacing floor plan:', error);
      toast.error('There was an error replacing your floor plan. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setCurrentFile(null);
    }
  };

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={open => !open && onClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace Floor Plan Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to replace this floor plan image? All your data will be
              preserved, but the new image will replace the current one.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="my-4 flex flex-col items-center">
            <Button
              onClick={handleFileClick}
              disabled={isLoading}
              className="flex items-center gap-2 w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {isLoading ? 'Uploading...' : 'Choose Image or PDF'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">Supported formats: PNG, JPG, PDF</p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*,application/pdf"
        className="hidden"
      />

      {isLoading && loadingMessage && (
        <LoadingOverlay message={loadingMessage} progress={progress} />
      )}

      <PDFPageSelectionModal
        isOpen={isPDFModalOpen}
        onClose={() => {
          setIsPDFModalOpen(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        pages={pdfPages}
        onPageSelect={handlePageSelect}
        onPageNameChange={handlePageNameChange}
        onContinue={handlePDFContinue}
        isLoading={isLoading}
        progress={progress}
        fileName={floorPlan.name}
        singlePageSelection={true}
      />
    </>
  );
}
